import { sanitizeRolexContent } from "../utils/rolexCompliance";

export const INDEXABLE_AUSTRALIAN_CITY_SLUGS = [
  "sydney",
  "melbourne",
  "brisbane",
  "perth",
  "adelaide",
  "hobart",
  "darwin",
  "canberra",
  "gold-coast"
];

const INDEXABLE_CITY_SET = new Set(INDEXABLE_AUSTRALIAN_CITY_SLUGS);

export function isIndexableAustralianCity(slug) {
  return INDEXABLE_CITY_SET.has(slug);
}

export function getCityLandingPath(slug) {
  return `/luxury-watches-${slug}`;
}

export function getCitySellConsignPath(slug) {
  return `/sell-your-watch-${slug}`;
}

const getCityLandingMetaDescription = (cityName) =>
  `Buy authenticated Patek Philippe, Audemars Piguet and Richard Mille watches in ${cityName}, or sell your watch with secure offers and insured handling.`;

const getCitySellMetaDescription = (cityName) =>
  `Sell your luxury watch in ${cityName} with Lux & Co. Request an instant market-based offer with insured handling and instant payment after authentication.`;

const DEFAULT_CITY_MEDIA = {
  hero: "/img/slideimage2.jpg",
  secondaryHero: "/img/slideimage3.jpg",
  sellHeroDesktop: "/testTEST.jpg",
  sellHeroMobile: "/SellConsignBg2.png"
};

const AUSTRALIAN_APPOINTMENT_ADDRESS = "Level 12, 440 Collins St, Melbourne VIC 3000";
const getCityAppointmentCopy = (cityName) =>
  `Lux & Co's Australian appointment address is ${AUSTRALIAN_APPOINTMENT_ADDRESS}. ${cityName} clients can message on WhatsApp before travelling to confirm an in-person appointment, collection or secure insured delivery.`;

const withLocalSeoConfig = (city) => ({
  ...city,
  city: city.name,
  country: "Australia",
  countryCode: "AU",
  cityPath: getCityLandingPath(city.slug),
  sellPath: getCitySellConsignPath(city.slug),
  metaTitle: city.metaTitle || `Lux & Co | Buy & Sell Luxury Watches ${city.name}`,
  metaDescription: city.metaDescription || getCityLandingMetaDescription(city.name),
  sellMetaTitle: city.sellMetaTitle || `Sell Your Luxury Watch ${city.name}`,
  sellMetaDescription: city.sellMetaDescription || getCitySellMetaDescription(city.name),
  heroHeading: city.heroHeading || `Buy & Sell Luxury Watches in ${city.name}`,
  isIndexable: isIndexableAustralianCity(city.slug),
  media: {
    ...DEFAULT_CITY_MEDIA,
    ...(city.media || {})
  }
});

const CITY_ENTRIES = [
  {
    slug: "sydney",
    name: "Sydney",
    state: "NSW",
    region: "New South Wales",
    heroSubheading: "Authenticated Patek Philippe, Audemars Piguet and Richard Mille watches delivered to Sydney, with a secure process for sellers across New South Wales.",
    serviceAreas: "Sydney CBD, Eastern Suburbs, North Shore, Inner West, Parramatta, Sutherland Shire and wider New South Wales",
    buyerAngle: "Sydney buyers can compare live authenticated inventory online before enquiring, with purchase support focused on reference, condition, box and papers status, delivery timing and secure handover.",
    sellerAngle: "Sydney sellers can begin with photos, reference details, condition notes and box or papers information, then proceed through insured handling and authentication before payment.",
    localHandover: getCityAppointmentCopy("Sydney"),
    serviceHighlights: [
      "Insured courier guidance for high-value watches from Sydney and NSW.",
      "Seller reviews cover Patek Philippe Nautilus and Aquanaut, Audemars Piguet Royal Oak, Richard Mille and other important Swiss references.",
      "Buyer enquiries focus on current availability, authentication, delivery timing and close alternatives when a reference is not listed."
    ],
    media: { hero: "/img/slideimage2.jpg", secondaryHero: "/img/slideimage3.jpg" },
    faqs: [
      {
        question: "Can I buy a luxury watch in Sydney from Lux & Co?",
        answer: "Yes. Sydney clients can enquire online about authenticated watches and confirm insured delivery or handover options before purchase."
      },
      {
        question: "Can I sell a watch from Sydney without visiting Melbourne?",
        answer: "Yes. Start online with photos and watch details. Lux & Co will confirm the next handling step before authentication and payment."
      },
      {
        question: "Can I arrange an in-person Sydney appointment?",
        answer: "Appointments or collection can be arranged at Lux & Co's Melbourne appointment address by prior arrangement. Message before travelling so the watch and timing can be confirmed."
      }
    ]
  },
  {
    slug: "melbourne",
    name: "Melbourne",
    state: "VIC",
    region: "Victoria",
    heroSubheading: "Authenticated luxury watches for Melbourne buyers and a secure sell-your-watch process for Victorian sellers.",
    serviceAreas: "Melbourne CBD, Southbank, Toorak, South Yarra, Brighton, Richmond and wider Victoria",
    buyerAngle: "Melbourne buyers can compare Patek Philippe, Audemars Piguet and Richard Mille pieces by reference, material, dial, year, box and papers status and current availability.",
    sellerAngle: "Melbourne sellers can submit watch details online and confirm secure handling options with Lux & Co before authentication and payment.",
    localHandover: getCityAppointmentCopy("Melbourne"),
    serviceHighlights: [
      "Melbourne-focused page for buyers comparing authenticated luxury watches in Victoria.",
      "Seller support covers instant market-based offers, secure handling, authentication and instant payment once your quote is accepted.",
      "Internal links connect Melbourne clients to the national sell hub and the Patek, Audemars Piguet and Richard Mille seller pages."
    ],
    media: { hero: "/img/slideimage3.jpg", secondaryHero: "/img/slideimage1.png" },
    faqs: [
      {
        question: "Can I sell my luxury watch in Melbourne?",
        answer: "Yes. Submit photos and watch details online, then Lux & Co will confirm the secure handling, authentication and payment sequence."
      },
      {
        question: "What Melbourne watch details should I submit?",
        answer: "Include the brand, model, reference, condition, year, service history, box and papers status, and clear photos of the dial, case, clasp and documents."
      },
      {
        question: "Can Melbourne buyers view current luxury watch availability online?",
        answer: "Yes. The catalog shows live authenticated inventory, with enquiry support for current availability, delivery timing and sourcing alternatives."
      }
    ]
  },
  {
    slug: "brisbane",
    name: "Brisbane",
    state: "QLD",
    region: "Queensland",
    heroSubheading: "Authenticated luxury watches delivered to Brisbane and secure seller support for Queensland clients.",
    serviceAreas: "Brisbane CBD, Fortitude Valley, New Farm, Ascot, Hamilton, South Bank and wider Queensland",
    buyerAngle: "Brisbane buyers often need national access to high-value Patek Philippe, Audemars Piguet and Richard Mille inventory that may be limited locally.",
    sellerAngle: "Brisbane sellers can request a market-based offer online and use secure insured handling for authentication before payment is completed.",
    localHandover: getCityAppointmentCopy("Brisbane"),
    serviceHighlights: [
      "Queensland-focused delivery and seller-handling guidance.",
      "Useful for buyers comparing scarce Richard Mille, Royal Oak, Nautilus and Aquanaut availability.",
      "Seller pages link directly into Patek Philippe, Audemars Piguet and Richard Mille valuation guidance."
    ],
    media: { hero: "/img/full-collection-desktop-1.png", secondaryHero: "/img/APFINAL.png" },
    faqs: [
      {
        question: "Can Lux & Co deliver luxury watches to Brisbane?",
        answer: "Yes. Brisbane clients can enquire about insured delivery timing and purchase support before completing a transaction."
      },
      {
        question: "Can I sell my watch from Brisbane?",
        answer: "Yes. Submit photos and documentation online so Lux & Co can review the watch and confirm secure handling before authentication."
      },
      {
        question: "Which brands does Lux & Co focus on for Brisbane sellers?",
        answer: "Lux & Co prioritises Patek Philippe, Audemars Piguet, Richard Mille and other authenticated Swiss luxury watches."
      }
    ]
  },
  {
    slug: "perth",
    name: "Perth",
    state: "WA",
    region: "Western Australia",
    heroSubheading: "National luxury watch access for Perth buyers and secure seller handling for Western Australian clients.",
    serviceAreas: "Perth CBD, Subiaco, Cottesloe, Claremont, Fremantle, West Perth and wider Western Australia",
    buyerAngle: "Perth buyers can use Lux & Co to compare national inventory, request current availability and confirm insured delivery details before purchase.",
    sellerAngle: "Perth sellers can start with photos and documents, then confirm insured courier handling or an in-person appointment before authentication and payment.",
    localHandover: getCityAppointmentCopy("Perth"),
    serviceHighlights: [
      "Built for long-distance buyer and seller handling from Western Australia.",
      "Clearer path for high-value watches where local availability is thin.",
      "Patek Philippe, Audemars Piguet and Richard Mille enquiries are routed into specialist brand pages."
    ],
    media: { hero: "/img/ProductLastDesk.png", secondaryHero: "/img/PATEKFINAL.png" },
    faqs: [
      {
        question: "Can I buy a luxury watch from Perth?",
        answer: "Yes. Perth buyers can enquire online and confirm insured delivery, current availability and any sourcing options with Lux & Co."
      },
      {
        question: "How do Perth sellers send a watch for review?",
        answer: "Begin with the online form and photos. Lux & Co will confirm the appropriate insured handling process before authentication."
      },
      {
        question: "Can Perth clients arrange an in-person appointment?",
        answer: "Appointments can be arranged at Lux & Co's Melbourne appointment address by prior arrangement. Message before travelling so the watch and timing can be confirmed."
      }
    ]
  },
  {
    slug: "adelaide",
    name: "Adelaide",
    state: "SA",
    region: "South Australia",
    heroSubheading: "Authenticated Patek Philippe, Audemars Piguet and Richard Mille watches for Adelaide buyers and sellers.",
    serviceAreas: "Adelaide CBD, North Adelaide, Norwood, Unley, Glenelg, Burnside and wider South Australia",
    buyerAngle: "Adelaide buyers can compare authenticated watches online by reference, condition, documentation and availability, then confirm delivery before purchase.",
    sellerAngle: "Adelaide sellers can request an offer by submitting the reference, condition, box and papers status, service history and clear images.",
    localHandover: getCityAppointmentCopy("Adelaide"),
    serviceHighlights: [
      "Designed for South Australian buyers comparing national availability.",
      "Seller guidance covers documentation, condition, service history and authentication.",
      "Brand links prioritise Patek Philippe, Audemars Piguet and Richard Mille."
    ],
    media: { hero: "/img/cardbackground3.png", secondaryHero: "/img/RMFINAL.png" },
    faqs: [
      {
        question: "Can Lux & Co deliver watches to Adelaide?",
        answer: "Yes. Adelaide clients can confirm insured delivery and purchase support before completing a watch enquiry."
      },
      {
        question: "Can I sell a luxury watch from Adelaide?",
        answer: "Yes. Start online with photos, reference details, condition notes and documents so Lux & Co can review the watch."
      },
      {
        question: "What should Adelaide sellers photograph?",
        answer: "Photograph the dial, case sides, caseback, clasp, bracelet, warranty card, box, papers and any service records."
      }
    ]
  },
  {
    slug: "hobart",
    name: "Hobart",
    state: "TAS",
    region: "Tasmania",
    heroSubheading: "Luxury watch buying and selling support for Hobart and Tasmanian clients using secure Australia-wide handling.",
    serviceAreas: "Hobart, Battery Point, Sandy Bay, Kingston, Glenorchy, Launceston and wider Tasmania",
    buyerAngle: "Hobart buyers can compare national authenticated inventory where local high-value watch availability may be limited.",
    sellerAngle: "Hobart sellers can complete the first valuation step online, then confirm insured handling or an in-person appointment before authentication and payment.",
    localHandover: getCityAppointmentCopy("Hobart"),
    serviceHighlights: [
      "Keeps the existing Hobart search opportunity while making appointment and insured-delivery options clear.",
      "Useful for Tasmanian clients comparing scarce Patek Philippe, Audemars Piguet and Richard Mille inventory.",
      "Seller guidance makes authentication and insured handling clear before a watch leaves the client."
    ],
    media: { hero: "/img/AP-Brand-Image.png", secondaryHero: "/img/newpatekbrand.JPG" },
    faqs: [
      {
        question: "Can Hobart clients buy from Lux & Co?",
        answer: "Yes. Hobart clients can enquire online about authenticated inventory and confirm insured delivery before purchase."
      },
      {
        question: "Can I sell a watch from Tasmania?",
        answer: "Yes. Submit photos and watch details first. Lux & Co will confirm handling and authentication steps before payment."
      },
      {
        question: "Can Hobart clients arrange an in-person appointment?",
        answer: "Appointments can be arranged at Lux & Co's Melbourne appointment address by prior arrangement. Message before travelling so the watch and timing can be confirmed."
      }
    ]
  },
  {
    slug: "darwin",
    name: "Darwin",
    state: "NT",
    region: "Northern Territory",
    heroSubheading: "Authenticated luxury watch access for Darwin clients with clear buying and selling support.",
    serviceAreas: "Darwin CBD, Cullen Bay, Fannie Bay, Palmerston, Casuarina and wider Northern Territory",
    buyerAngle: "Darwin buyers can use Lux & Co to access national authenticated inventory and confirm delivery, condition and availability before purchase.",
    sellerAngle: "Darwin sellers can begin with an online submission, then confirm insured handling and authentication before payment is sent.",
    localHandover: getCityAppointmentCopy("Darwin"),
    serviceHighlights: [
      "Supports NT clients where local high-value luxury watch stock can be difficult to compare.",
      "Seller process supports online review, insured handling and appointment coordination with clear authentication and payment sequencing.",
      "Buyer enquiries connect Darwin clients to live catalog pages and sourcing conversations."
    ],
    media: { hero: "/img/newpatekbrand.JPG", secondaryHero: "/img/COOLNEWPATEKIMAGE.png" },
    faqs: [
      {
        question: "Can Lux & Co deliver luxury watches to Darwin?",
        answer: "Yes. Darwin clients can enquire about authenticated watches and confirm insured delivery details before purchase."
      },
      {
        question: "Can I sell a watch from Darwin?",
        answer: "Yes. Submit photos, reference details and documents online so Lux & Co can review the watch before arranging handling."
      },
      {
        question: "Can Darwin clients arrange an in-person appointment?",
        answer: "Appointments can be arranged at Lux & Co's Melbourne appointment address by prior arrangement. Message before travelling so the watch and timing can be confirmed."
      }
    ]
  },
  {
    slug: "canberra",
    name: "Canberra",
    state: "ACT",
    region: "Australian Capital Territory",
    heroSubheading: "Secure luxury watch buying and selling support for Canberra and ACT clients.",
    serviceAreas: "Canberra CBD, Barton, Kingston, Manuka, Braddon, Deakin and the wider ACT",
    buyerAngle: "Canberra buyers can compare authenticated Patek Philippe, Audemars Piguet and Richard Mille watches online and confirm availability before purchase.",
    sellerAngle: "Canberra sellers can request an offer with photos, documentation, condition details and reference information before secure authentication.",
    localHandover: getCityAppointmentCopy("Canberra"),
    serviceHighlights: [
      "Designed for discreet enquiries and appointment coordination from Canberra and the ACT.",
      "Helpful for sellers of documented, high-value references where provenance and condition matter.",
      "Buyer links route directly to current brand and model inventory."
    ],
    media: { hero: "/img/newrmbrand.png", secondaryHero: "/img/RM-Product-Slider.png" },
    faqs: [
      {
        question: "Can Canberra buyers purchase from Lux & Co?",
        answer: "Yes. Canberra buyers can enquire online and confirm authentication, availability and insured delivery before purchase."
      },
      {
        question: "Can Canberra sellers use the sell-your-watch service?",
        answer: "Yes. Submit watch details online so Lux & Co can review the reference, condition and documentation before arranging handling."
      },
      {
        question: "Can Canberra clients arrange an in-person appointment?",
        answer: "Appointments can be arranged at Lux & Co's Melbourne appointment address by prior arrangement. Message before travelling so the watch and timing can be confirmed."
      }
    ]
  },
  {
    slug: "gold-coast",
    name: "Gold Coast",
    state: "QLD",
    region: "Queensland",
    heroSubheading: "Authenticated luxury watches for Gold Coast buyers and secure seller support across South East Queensland.",
    serviceAreas: "Surfers Paradise, Broadbeach, Main Beach, Southport, Burleigh Heads, Sanctuary Cove and wider South East Queensland",
    buyerAngle: "Gold Coast buyers can compare authenticated statement pieces and scarce references online before confirming availability and delivery.",
    sellerAngle: "Gold Coast sellers can submit watch details, documents and photos online before secure handling, authentication and payment.",
    localHandover: getCityAppointmentCopy("Gold Coast"),
    serviceHighlights: [
      "Useful for high-value seller enquiries from South East Queensland.",
      "Buyer support focuses on current availability, authentication and delivery timing.",
      "Links connect clients to Patek Philippe, Audemars Piguet and Richard Mille buying and selling paths."
    ],
    media: { hero: "/img/APFINAL.png", secondaryHero: "/img/AP-Brand-Image.png" },
    faqs: [
      {
        question: "Can Lux & Co deliver watches to the Gold Coast?",
        answer: "Yes. Gold Coast buyers can confirm insured delivery and current availability before purchase."
      },
      {
        question: "Can I sell a luxury watch from the Gold Coast?",
        answer: "Yes. Start with photos, reference details, box and papers status and condition notes so Lux & Co can assess the watch."
      },
      {
        question: "Can Gold Coast clients arrange an in-person appointment?",
        answer: "Appointments can be arranged at Lux & Co's Melbourne appointment address by prior arrangement. Message before travelling so the watch and timing can be confirmed."
      }
    ]
  }
];

export const AUSTRALIAN_CITIES = CITY_ENTRIES
  .map(withLocalSeoConfig)
  .map(sanitizeRolexContent);

export const findAustralianCityBySlug = (slug) =>
  AUSTRALIAN_CITIES.find((city) => city.slug === slug);
